import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, X } from 'lucide-react'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { TextField } from 'vnetwork-ui'

// Matches `(*.)example.com` — same regex as MultiValueInput's domain
// branch — kept local so the card stays self-contained.
const HOSTNAME_RE =
    /^(?:\*\.)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i

interface Props<T extends FieldValues> {
    control: Control<T>
    name?: FieldPath<T>
}

// Card wrapper for the optional domain list input. Visually mirrors
// <ServiceReportMailerHint /> — same border + muted bg + header icon —
// so the recipients card + this card read as one "report scope" pair
// at the bottom of the form.
//
// Rolls its own input + chip rendering instead of reusing
// <MultiValueInput /> because we need the chips to match the inline
// `<span>`-based style in ServiceReportMailerHint rather than the
// heavier `vnetwork-ui` Chip widget used elsewhere.
const DomainsFilterCard = <T extends FieldValues>({
    control,
    name = 'domains' as FieldPath<T>,
}: Props<T>) => {
    const { t } = useTranslation('report_waf')
    const { field } = useController({ control, name })
    const values: string[] = Array.isArray(field.value) ? field.value : []
    const [draft, setDraft] = useState('')
    const [error, setError] = useState('')

    const addFromDraft = () => {
        const items = draft
            .split(/[,\n;]/)
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean)
        if (!items.length) return

        const next: string[] = []
        for (const item of items) {
            if (values.includes(item) || next.includes(item)) continue
            if (!HOSTNAME_RE.test(item)) {
                setError(
                    t('validation.domain', {
                        defaultValue:
                            'Domain không hợp lệ (ví dụ: example.com hoặc *.example.com)',
                    })
                )
                return
            }
            next.push(item)
        }

        if (next.length > 0) {
            field.onChange([...values, ...next])
            setDraft('')
            setError('')
        }
    }

    const removeAt = (i: number) =>
        field.onChange(values.filter((_, idx) => idx !== i))

    return (
        <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-1.5 text-sm font-medium">
                    <Globe className="w-3.5 h-3.5" />
                    {t('fields.domains')}{' '}
                    <span className="font-normal text-muted-foreground">
                        ({t('optional', { defaultValue: 'Optional' })})
                    </span>
                </label>
            </div>

            {/* Input spans the card's full inner width. Commit triggers
                are Enter / comma / semicolon / blur — the explicit "Add"
                button was dropped because those 4 triggers already cover
                every realistic add-flow and the button was just eating
                horizontal space. */}
            <div className="mb-2 w-full">
                <TextField
                    value={draft}
                    placeholder={t('report_waf.placeholders.domains', {
                        defaultValue:
                            'e.g. example.com, *.example.com (press Enter or comma to add)',
                    })}
                    onChange={(e) => {
                        setDraft(e.target.value)
                        if (error) setError('')
                    }}
                    onKeyDown={(e) => {
                        if (['Enter', ',', ';'].includes(e.key)) {
                            e.preventDefault()
                            addFromDraft()
                        }
                    }}
                    onBlur={addFromDraft}
                    size="small"
                    className="w-full"
                    error={!!error}
                    helperText={error || ''}
                />
            </div>

            {values.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {values.map((domain, i) => (
                        <span
                            key={domain}
                            className="inline-flex items-center gap-1 rounded border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs text-primary"
                        >
                            {domain}
                            <button
                                type="button"
                                onClick={() => removeAt(i)}
                                className="text-primary/60 hover:text-primary"
                                aria-label={`remove ${domain}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-muted-foreground">
                    {t('domains_helper', {
                        defaultValue:
                            'Leave empty to report all domains. Add specific domains to limit the report scope.',
                    })}
                </p>
            )}
        </div>
    )
}

export default DomainsFilterCard
